import { useEffect } from "react";
import { Link } from "react-router-dom";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapContainer, Marker, Popup, TileLayer, useMap } from "react-leaflet";
import { useI18n } from "../lib/i18n";
import { fmtNum } from "../lib/format";
import { PriceTag } from "./PropertyCard";
import { mediaUrl } from "../lib/api";

const TILES = "https://tile.openstreetmap.org/{z}/{x}/{y}.png";
const ATTR = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

const pin = (text, small) => L.divIcon({ className: "", html: `<div class="lf-marker ${small ? "lf-marker-sm" : ""}" style="width:34px;height:34px">${text}</div>`, iconSize: small ? [18, 18] : [34, 34], iconAnchor: small ? [9, 9] : [17, 17] });

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (points.length > 1) map.fitBounds(points, { padding: [50, 50], maxZoom: 12 });
    else if (points.length === 1) map.setView(points[0], 13);
  }, [map, JSON.stringify(points)]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

const Base = ({ children, className, center = [23.8, 44.5], zoom = 5, testid }) => (
  <div data-testid={testid} className={`overflow-hidden rounded-sm border border-cta/20 ${className}`}>
    <MapContainer center={center} zoom={zoom} scrollWheelZoom={false} className="h-full w-full">
      <TileLayer url={TILES} attribution={ATTR} className="lf-dark-tiles" />
      {children}
    </MapContainer>
  </div>
);

export function CityMap({ cities, className = "h-[520px]" }) {
  const { t, tr, lang } = useI18n();
  return (
    <Base className={className} testid="saudi-city-map">
      {cities.map((c) => (
        <Marker key={c.slug} position={[c.lat, c.lng]} icon={pin(fmtNum(c.properties + c.projects, lang))} title={tr(c, "name")}>
          <Popup>
            <div className="min-w-[180px]" dir={lang === "ar" ? "rtl" : "ltr"}>
              <p className="font-display text-lg text-cta">{tr(c, "name")}</p>
              <p className="mb-3 text-xs text-fg/60">{fmtNum(c.properties, lang)} {t("map.properties")} · {fmtNum(c.projects, lang)} {t("map.projects")}</p>
              <div className="flex flex-col gap-1.5 text-sm">
                <Link to={`/properties?city=${c.slug}`} data-testid={`map-city-properties-${c.slug}`} className="!text-cta hover:underline">{t("map.viewProperties")} →</Link>
                {c.projects > 0 && <Link to={`/developments?city=${c.slug}`} data-testid={`map-city-projects-${c.slug}`} className="!text-fg hover:underline">{t("map.viewProjects")} →</Link>}
              </div>
            </div>
          </Popup>
        </Marker>
      ))}
    </Base>
  );
}

export function PropertiesMap({ items, className = "h-[600px]", single = false }) {
  const { tr, lang } = useI18n();
  const points = items.filter((p) => p.lat && p.lng).map((p) => [p.lat, p.lng]);
  return (
    <Base className={className} testid={single ? "property-location-map" : "listing-map"}>
      <FitBounds points={points} />
      {items.filter((p) => p.lat && p.lng).map((p) => (
        <Marker key={p.id || p.slug} position={[p.lat, p.lng]} icon={pin("", true)} title={tr(p, "title")}>
          {!single && (
            <Popup>
              <Link to={`/properties/${p.slug}`} className="block w-52" dir={lang === "ar" ? "rtl" : "ltr"} data-testid={`map-marker-link-${p.slug}`}>
                {p.images?.[0] && <img src={mediaUrl(p.images[0])} alt="" className="mb-2 h-24 w-full rounded-sm object-cover" />}
                <p className="text-sm font-semibold !text-fg">{tr(p, "title")}</p>
                <PriceTag p={p} className="text-sm !text-cta" />
              </Link>
            </Popup>
          )}
        </Marker>
      ))}
    </Base>
  );
}
